import {
  Activity,
  Blocks,
  BookOpenText,
  Bot,
  ChartGantt,
  Database,
  FileSpreadsheet,
  FileText,
  GitBranch,
  GitMerge,
  Globe,
  LayoutGrid,
  type LucideIcon,
  MapPin,
  Search,
  Smartphone,
  Users,
  Waypoints,
} from 'lucide-react'
import { MOBILE_FORENSICS_EXTENSION_ID, OSINT_EXTENSION_ID } from '@/hooks/use-extensions'

export type WorkspaceKey =
  | 'overview'
  | 'agent'
  | 'documents'
  | 'caseSchema'
  | 'investigate'
  | 'monitor'
  | 'report'

export interface SubView {
  /** Flat segment under `/cases/:caseId/`, e.g. 'identity', 'extraction-runs'. */
  path: string
  label: string
  icon: LucideIcon
  /** When set, the sub-view renders only while this extension is enabled. */
  extensionId?: string
}

export interface Workspace {
  key: WorkspaceKey
  /** Sidebar group header — not part of the URL. */
  label: string
  icon: LucideIcon
  subViews: SubView[]
}

export const WORKSPACES: Workspace[] = [
  {
    key: 'overview',
    label: 'Overview',
    icon: LayoutGrid,
    subViews: [
      { path: 'overview', label: 'Overview', icon: LayoutGrid },
      { path: 'investigations', label: 'Investigations', icon: BookOpenText },
    ],
  },
  {
    key: 'agent',
    label: 'Xendrel Assistant',
    icon: Bot,
    subViews: [{ path: 'agent', label: 'Xendrel Assistant', icon: Bot }],
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: FileText,
    subViews: [
      { path: 'documents', label: 'Documents', icon: Database },
      { path: 'extraction-runs', label: 'Extraction Runs', icon: GitBranch },
    ],
  },
  {
    key: 'caseSchema',
    label: 'Schema',
    icon: Blocks,
    subViews: [{ path: 'schema', label: 'Schema', icon: Blocks }],
  },
  {
    key: 'investigate',
    label: 'Investigate',
    icon: Search,
    subViews: [
      { path: 'search', label: 'Search', icon: Search },
      { path: 'osint', label: 'OSINT', icon: Globe, extensionId: OSINT_EXTENSION_ID },
      {
        path: 'comms',
        label: 'Mobile Forensics',
        icon: Smartphone,
        extensionId: MOBILE_FORENSICS_EXTENSION_ID,
      },
      { path: 'structured-ingest', label: 'Structured ingest', icon: FileSpreadsheet },
      { path: 'identity', label: 'Identity', icon: Users },
      { path: 'entity-resolution', label: 'Entity Resolution', icon: GitMerge },
      { path: 'geolocation', label: 'Map', icon: MapPin },
      { path: 'graph', label: 'Graph', icon: Waypoints },
      { path: 'time-axis', label: 'Timeline', icon: ChartGantt },
    ],
  },
  {
    key: 'monitor',
    label: 'Monitor',
    icon: Activity,
    subViews: [{ path: 'monitor', label: 'Monitor', icon: Activity }],
  },
  {
    key: 'report',
    label: 'Report',
    icon: FileText,
    subViews: [
      { path: 'case-summary', label: 'Case Summary', icon: FileText },
      // hidden for now
      // { path: 'evidence', label: 'Evidence Package', icon: Database },
      // { path: 'audit', label: 'Audit Trail', icon: Clock },
    ],
  },
]

/**
 * WORKSPACES with the sub-views of disabled extensions removed, and the sub-views the
 * case's settings hide from the menu (their routes stay reachable — a hidden view is
 * opened from wherever it belongs, e.g. structured ingest from a document's detail).
 */
export function visibleWorkspaces(
  enabledExtensions: ReadonlySet<string>,
  hiddenPaths: ReadonlySet<string> = new Set()
): Workspace[] {
  return WORKSPACES.map(ws => ({
    ...ws,
    subViews: ws.subViews.filter(
      sv =>
        (sv.extensionId == null || enabledExtensions.has(sv.extensionId)) &&
        !hiddenPaths.has(sv.path)
    ),
  })).filter(ws => ws.subViews.length > 0)
}

/** The menu entries a case's settings switch off. */
export function hiddenSubViewPaths(
  settings: { show_structured_ingest?: boolean } | undefined
): ReadonlySet<string> {
  return settings?.show_structured_ingest === false ? new Set(['structured-ingest']) : new Set()
}

export function resolveSubViewUrl(caseId: string, sub: SubView): string {
  return `/cases/${caseId}/${sub.path}`
}

/** Reserved top-level segments under `/cases/` that are NOT case ids. */
export const RESERVED_CASE_SEGMENTS = new Set(['home', 'list'])
